import React, { useState, useEffect } from 'react';
import { projectService } from '../../services/projectService';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
} from '@mui/material';
import { format } from 'date-fns';
import {
    projectListPrimaryLabel,
} from '../../utils/projectIdentityFields';
import PageHeader from '../common/PageHeader';
import StatusBadge from '../common/StatusBadge';
import EmptyState from '../common/EmptyState';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import { shadow } from '../../theme/tokens';

// Read-only: projects are sourced from IAM/Hub sync. This page browses and opens
// them, it does not create, edit, or delete either projects or studies.
const ProjectList = () => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const navigate = useNavigate();

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const response = await projectService.listProjects();
            setProjects(response.data);
            setError(null);
        } catch (err) {
            setError('Failed to fetch projects');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProjects();
    }, []);

    const handleProjectClick = (projectId) => {
        navigate(`/projects/${projectId}`);
    };

    if (loading) {
        return (
            <Box sx={{ p: 3, mt: 2 }}>
                <LoadingState label="Loading projects…" />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3, mt: 2 }}>
                <ErrorState message={error} onRetry={fetchProjects} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, mt:2 }}>
            <PageHeader title="Projects" />

            {projects.length === 0 ? (
                <EmptyState title="No projects yet" description="Projects synced from IAM will show up here." />
            ) : (
            <Grid container spacing={2.5}>
                {projects.map((project) => (
                    <Grid item xs={12} sm={6} md={4} key={project._id}>
                        <Card
                            sx={{
                                cursor: 'pointer',
                                height: '100%',
                                '&:hover': {
                                    boxShadow: shadow.md,
                                    borderColor: 'text.disabled',
                                },
                            }}
                            onClick={() => handleProjectClick(project._id)}
                        >
                            <CardContent>
                                <Typography variant="h6" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                                    {projectListPrimaryLabel(project)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    Created {format(new Date(project.createdAt), 'MMM dd, yyyy')}
                                </Typography>
                                {project.status && (
                                    <Box sx={{ mt: 1.5 }}>
                                        <StatusBadge status={project.status} />
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
            )}
        </Box>
    );
};

export default ProjectList;
